import { first, flatMapDeep, flattenDeep, groupBy, join, last } from 'lodash';
import { writeClipboard } from './clipboard';
import Tooltip from '../../../../../components/bases/tooltips/tooltip';

// ***************************************************************************************
// calculateTime est une fonction permet de calculer la durée d'un nouveau mot
// ***************************************************************************************
export function calculateTime(startTime, endTime, oldText, newText) {
  const duration = endTime - startTime;
  const time = (duration / oldText.length) * newText.length;
  const inOutTime = parseFloat((startTime + time).toFixed(2));

  if (isNaN(inOutTime)) {
    return endTime;
  }

  return inOutTime;
}

// ***************************************************************************************
// getWordByCoordinates()
// Permet de trouver le mot le plus proche des coordonnées X et Y
// ***************************************************************************************
export function getWordByCoordinates(lines, offsetX, offsetY) {
  const words = flattenDeep(lines.map((x) => x.words));

  let word = words.find(
    (word) =>
      word.startX <= offsetX &&
      offsetX <= word.endX &&
      word.startY <= offsetY &&
      offsetY <= word.endY
  );

  return word;
}

// ***************************************************************************************
// getWordByCoordinatesOutside()
// Permet de trouver le mot le plus proche en dehors des coordonnées x et y
// ***************************************************************************************
export function getWordByCoordinatesOutside(lines, offsetX, offsetY) {
  let minDistance = Infinity;
  let closestWord = null;

  // Trouver la ligne la plus proche
  let minLineDistance = Infinity;
  let closestLine = null;
  for (const line of lines) {
    const dy = (line.startY ?? 0) - offsetY;
    const lineDistance = Math.abs(dy);
    if (lineDistance < minLineDistance) {
      minLineDistance = lineDistance;
      closestLine = line;
    }
  }

  // Trouver le mot le plus proche dans la ligne la plus proche
  if (closestLine) {
    for (const word of closestLine.words) {
      const dxStart = word.startX - offsetX;
      const dxEnd = word.endX - offsetX;
      const dyStart = word.startY - offsetY;
      const dyEnd = word.endY - offsetY;
      // Utilisation de la distance minimale parmi les coins
      const distances = [
        Math.sqrt(dxStart * dxStart + dyStart * dyStart),
        Math.sqrt(dxEnd * dxEnd + dyStart * dyStart),
        Math.sqrt(dxStart * dxStart + dyEnd * dyEnd),
        Math.sqrt(dxEnd * dxEnd + dyEnd * dyEnd),
      ];
      const distance = Math.min(...distances);
      if (distance < minDistance) {
        minDistance = distance;
        closestWord = word;
      }
    }
  }

  return { closestLine, closestWord };
}

export function getWordIndexByCoordinates(words, offsetX, offsetY) {
  let index = words.findIndex(
    (word) =>
      word.startX <= offsetX &&
      offsetX <= word.endX &&
      word.startY <= offsetY &&
      offsetY <= word.endY
  );

  return index;
}

const markWordKeys = [
  {
    key: 'CHECKWORD',
    value: 1,
  },
  {
    key: 'NEWST',
    value: 2,
  },
];

// ***************************************************************************************
// getMarkWord
// Récupérer le markword en fonction de la clé
// ***************************************************************************************
export function getMarkWord(key) {
  return markWordKeys.find((x) => x.key === key);
}

// ***************************************************************************************
// findMarkWords
// Trouver les bonnes clés avec la suite géométrique
// ***************************************************************************************
export function findMarkWords(target) {
  return markWordKeys.filter((item) => (target & item.value) !== 0);
}

// ***************************************************************************************
// copyToClipboard
// Copier dans le presse-papiers
// ***************************************************************************************
export function copyToClipboard(words) {
  let stringToCopy = '';

  for (let i = 0; i < words.length; i++) {
    const word = words[i];

    if (i !== 0 && !word.newline) {
      stringToCopy += ' ';
    }

    if (word.newline) {
      stringToCopy += '\n';
    }

    stringToCopy += word.text;
  }

  writeClipboard(stringToCopy);
}

// ***************************************************************************************
// isLetterUpperCase permet de définir si la lettre est en majuscule
// ***************************************************************************************
export function isLetterUpperCase(letter) {
  return letter === letter.toUpperCase();
}

// ***************************************************************************************
// isWordUpperCase permet de définir si le mot est en majuscule
// ***************************************************************************************
export function isWordUpperCase(word) {
  return word === word.toUpperCase();
}

// ***************************************************************************************
// removePunctuation permet de supprimer les ponctuations en fin de mot
// ***************************************************************************************
export function removePunctuation(text) {
  return text.toLowerCase().replace(/[.,?!]+$/, '');
}

// ***************************************************************************************
// hasPunctuation permet de vérifier si le texte a des ponctuations en fin de mot
// ***************************************************************************************
export function hasPunctuation(text) {
  const regex = /[.,?!]+$/;
  return regex.test(text);
}

// ***************************************************************************************
// isDigit permet de vérifier si le caractère est numérique
// ***************************************************************************************
export function isDigit(char) {
  const regex = /^\d+$/;
  return regex.test(char);
}

// ***************************************************************************************
// wait permet d'attendre un certain temps avant de continuer
// ***************************************************************************************
export function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ***************************************************************************************
// trapFocusElements permet de tabuler sur les éléments qu'on veut focused
// ***************************************************************************************
export function trapFocusElements(
  parentElement,
  currentElement,
  selector,
  shiftKey
) {
  // Obtenir tous les éléments focusables dans le component
  const elements = parentElement.querySelectorAll(selector);

  if (!elements.length) {
    return;
  }

  // Trouver l'index de l'élément focusé dans la liste des boutons
  const currentIndex = Array.prototype.indexOf.call(elements, currentElement);

  let nextIndex;

  if (shiftKey) {
    nextIndex = (currentIndex - 1) % elements.length;

    if (nextIndex === -1) {
      nextIndex = elements.length - 1;
    }
  } else {
    nextIndex = (currentIndex + 1) % elements.length;
  }

  var nextButton = elements[nextIndex];
  console.log("nextbutton", nextButton);
  if (nextButton) {
    nextButton.focus();
  }
}

export function isNativeInputKey(event) {
    const { key, ctrlKey, metaKey, altKey } = event;

    // Combinaisons natives : copier, coller, couper, sélectionner tout
    if (
      (ctrlKey || metaKey) &&
      ['a', 'c', 'v', 'x'].includes(key.toLowerCase())
    ) {
      return true;
    }

    // Raccourcis système ou de saisie avec Alt
    if (altKey) {
      return true;
    }

    // Touches natives de saisie et de navigation
    return (
      key.length === 1 ||
      [
        'Dead', // Touches mortes : ^, ¨, `, ~...
        'Backspace',
        'Delete',
        'ArrowLeft',
        'ArrowRight',
        'ArrowUp',
        'ArrowDown',
        'Shift',
        'Control',
        'Alt',
        'Meta',
        'CapsLock',
      ].includes(key)
    );
  };

// ***************************************************************************************
// cloneSubtitle() permet de cloner l'objet subtitle sans les propriétés créés ici
// ***************************************************************************************
export function cloneSubtitle(subtitle) {
  const {
    id,
    in: inTime,
    out,
    len,
    timecodeoutmaxi,
    timecodeinmini,
    timecodeinminitol,
    mark,
    lines,
    tcstatus,
    ststatus,
    textstatus,
  } = subtitle;

  let nbCharsTotal = 0;

  const linesCloned = lines.map((line) => {
    const wordsClones = line.words.map((word) => ({
      in: word.in,
      out: word.out,
      color: word.color,
      italic: word.italic,
      text: word.text,
      rtl: word.rtl ?? false,
    }));

    const nbChars = wordsClones.map((x) => x.text).join(' ').length;
    nbCharsTotal += nbChars;

    return {
      len: nbChars,
      verticalPos: line.verticalPos,
      horizontalPos: line.horizontalPos,
      align: line.align,
      words: wordsClones,
      speakerId: line.speakerId,
      doubleHeight: line.doubleHeight
    };
  });

  return {
    id,
    in: inTime,
    out,
    len: nbCharsTotal,
    lines: linesCloned,
    timecodeoutmaxi,
    timecodeinmini,
    timecodeinminitol,
    mark,
    tcstatus,
    ststatus,
    textstatus,
  };
}

export function getNewWordForSearch(
  caseSensitive,
  wholeWord,
  indexWord,
  word,
  text
) {
  let startIndex;
  if (wholeWord) {
    startIndex = 0;
  } else {
    if (caseSensitive) {
      startIndex = word.text.indexOf(text);
    } else {
      startIndex = word.text.toLowerCase().indexOf(text.toLowerCase());
    }
  }

  return {
    indexWord,
    inWord: word.in,
    text: word.text,
    startIndex,
    endIndex: startIndex + text.length,
  };
}

export function filterTextForSearch(
  subtitles,
  caseSensitive,
  wholeWord,
  text,
  callback
) {
  let wordsToSearch = [];

  subtitles.forEach((subtitle) => {
    const words = flatMapDeep(subtitle.lines.map((x) => x.words));

    const wordsFounded = words.filter(callback);

    if (wordsFounded.length) {
      wordsFounded.forEach((word) => {
        const index = words.indexOf(word);

        const newWord = getNewWordForSearch(
          caseSensitive,
          wholeWord,
          index,
          word,
          text
        );

        wordsToSearch.push({
          id: subtitle.id,
          in: subtitle.in,
          words: [newWord],
        });
      });
    }
  });

  return wordsToSearch;
}

export function filterTextMultipleForSearch(
  searchText,
  subtitle,
  words,
  caseSensitive
) {
  const searchFounded = [];

  const sentence = join(
    words.map((x) => x.text),
    ' '
  );

  const hay = caseSensitive ? sentence : sentence.toLowerCase();
  const needle = caseSensitive ? searchText : searchText.toLowerCase();

  // Récupérer tous les positions de départ (permet les occurrences chevauchantes)
  const startIndices = [];
  let pos = hay.indexOf(needle);
  while (pos !== -1) {
    startIndices.push(pos);
    pos = hay.indexOf(needle, pos + 1); // +1 pour autoriser chevauchement
  }

  if (startIndices.length === 0) {
    return [];
  }

  let indexLetter = 0;
  const letters = [];

  words.forEach((word, index) => {
    for (let i = 0; i < word.text.length; i++) {
      const char = word.text.charAt(i);

      const letter = {
        index: i,
        letter: char,
        indexLetter,
        indexWord: index,
        inWord: word.in,
        space: false,
        text: word.text,
      };

      letters.push(letter);

      indexLetter++;
    }

    const space = {
      index: word.text.length,
      letter: ' ',
      indexLetter,
      indexWord: index,
      inWord: word.in,
      space: true,
      text: word.text,
    };

    letters.push(space);

    indexLetter++;
  });

  // Pour chaque occurrence trouvée, construire les mots associés et les ajouter au résultat
  for (const startIndexLetter of startIndices) {
    const endIndexLetter = startIndexLetter + needle.length - 1;

    const foundLetters = letters.filter(
      (x) =>
        x.indexLetter >= startIndexLetter &&
        x.indexLetter <= endIndexLetter &&
        !x.space
    );

    const groupByIndexWord = groupBy(foundLetters, (x) => x.indexWord);

    const wordsFounded = [];

    Object.keys(groupByIndexWord).forEach((w) => {
      const values = groupByIndexWord[w];

      const firstValue = first(values);
      const lastValue = last(values);

      wordsFounded.push({
        indexWord: firstValue.indexWord,
        inWord: firstValue.inWord,
        text: firstValue.text,
        startIndex: firstValue.index,
        endIndex: lastValue.index + 1,
      });
    });

    if (wordsFounded.length) {
      searchFounded.push({
        id: subtitle.id,
        in: subtitle.in,
        words: wordsFounded,
      });
    }
  }

  return searchFounded;
}

// ***************************************************************************************
// removeEmptyLines supprime les lignes vides d'un sous-titre
// ***************************************************************************************
export function removeEmptyLines(subtitle) {
  subtitle.lines = subtitle.lines.filter((line) => line.len !== 0);
  subtitle.lines.forEach((x, index) => x.index = index);
}

// ***************************************************************************************
// replaceWordsWithDifferentCount() remplace searchText par replaceText dans les mots
// sélectionnés (consécutifs) quand le nombre de mots change : supprime les mots en trop
// ou ajoute les mots manquants (le temps est réparti avec calculateTime)
// ***************************************************************************************
export function replaceWordsWithDifferentCount(
  subtitle,
  wordsSelected,
  searchText,
  replaceText
) {
  const joined = wordsSelected.map((x) => x.text).join(' ');
  const newTexts = joined
    .replace(searchText, replaceText)
    .split(' ')
    .filter(Boolean);

  const keptCount = Math.min(newTexts.length, wordsSelected.length);

  // mots existants réutilisés
  for (let i = 0; i < keptCount; i++) {
    wordsSelected[i].text = newTexts[i];
  }

  const findLine = (word) =>
    subtitle.lines.find((line) => line.words.includes(word));

  if (newTexts.length < wordsSelected.length) {
    // on supprime les mots en trop
    const removed = wordsSelected.slice(keptCount);
    const lastKept = wordsSelected[keptCount - 1];
    if (lastKept) {
      lastKept.out = last(removed).out;
    }

    removed.forEach((w) => {
      const line = findLine(w);
      if (line) {
        line.words.splice(line.words.indexOf(w), 1);
      }
    });
    return;
  }

  // on ajoute les mots manquants après le dernier mot sélectionné
  const lastWord = last(wordsSelected);
  const line = findLine(lastWord);
  if (!line) {
    return;
  }

  const outTime = lastWord.out;
  let inOutTime = lastWord.in;
  let insertAt = line.words.indexOf(lastWord) + 1;

  // le dernier mot existant est raccourci pour laisser du temps aux nouveaux
  const extraTexts = newTexts.slice(keptCount);
  const allTexts = [lastWord.text, ...extraTexts];
  const totalText = allTexts.join('');
  const timings = allTexts.map((t) => {
    const start = inOutTime;
    inOutTime = calculateTime(start, outTime, totalText, t);
    return { start, end: inOutTime };
  });

  lastWord.out = timings[0].end;

  extraTexts.forEach((text, i) => {
    line.words.splice(insertAt++, 0, {
      ...lastWord,
      text,
      in: timings[i + 1].start,
      out: i === extraTexts.length - 1 ? outTime : timings[i + 1].end,
      letters: [],
    });
  });
}